import { toCamelCase } from 'case-converter';
import { RateSchema } from './schemas';
import { PackageSchema } from '@features/DynamicPackagesConfigurationWizard/types';
import { toPayableAmount } from '../rooms/helpers/toPayableAmount.helper';
import { AvailableRate, Rate, RateRoom, StaticRate } from './types';

/**
 * Orders rate rooms by priority ascending (lower number shown first). Rooms without a
 * priority are placed after all prioritised rooms and, among themselves, ordered by
 * the price the guest pays for the stay, ascending.
 */
export const byRoomPriorityThenPrice = (
  a: Pick<RateRoom, 'priority' | 'pricePerStay' | 'discount'>,
  b: Pick<RateRoom, 'priority' | 'pricePerStay' | 'discount'>
) => {
  const priorityA = a.priority ?? Infinity;
  const priorityB = b.priority ?? Infinity;
  if (priorityA !== priorityB) {
    return priorityA - priorityB;
  }
  return (
    (toPayableAmount(a.pricePerStay, a.discount) ?? Infinity) -
    (toPayableAmount(b.pricePerStay, b.discount) ?? Infinity)
  );
};

/**
 * Orders rate rooms with bookable rooms first (quantity is null/unknown or > 0),
 * sold-out rooms last (quantity === 0). Inside each group, priority/price ordering
 * from byRoomPriorityThenPrice applies.
 */
export const byRoomAvailabilityThenPriorityThenPrice = (
  a: Pick<RateRoom, 'priority' | 'pricePerStay' | 'quantity' | 'discount'>,
  b: Pick<RateRoom, 'priority' | 'pricePerStay' | 'quantity' | 'discount'>
) => {
  const aUnavailable = a.quantity === 0;
  const bUnavailable = b.quantity === 0;
  if (aUnavailable !== bUnavailable) {
    return aUnavailable ? 1 : -1;
  }
  return byRoomPriorityThenPrice(a, b);
};

const parsePackage = (rawPackage: unknown) => {
  if (!rawPackage) return null;
  const result = PackageSchema.safeParse(toCamelCase(rawPackage));
  if (!result.success) return null;
  return result.success ? result.data : null;
};

type MapToRates = (staticRates: StaticRate[], availableRates: AvailableRate[]) => Rate[];

/**
 * Orders rates by priority ascending (lower number shown first). Rates without a priority
 * are placed after all prioritised rates and, among themselves, ordered by the price the guest
 * pays, ascending.
 */
export const byPriorityThenPrice = (
  a: Pick<Rate, 'priority' | 'price' | 'discount'>,
  b: Pick<Rate, 'priority' | 'price' | 'discount'>
) => {
  const priorityA = a.priority ?? Infinity;
  const priorityB = b.priority ?? Infinity;
  if (priorityA !== priorityB) {
    return priorityA - priorityB;
  }
  // Compare for equality before subtracting: two rates with no price are both Infinity,
  // and Infinity - Infinity is NaN, which silently breaks Array.sort.
  const priceA = toPayableAmount(a.price, a.discount) ?? Infinity;
  const priceB = toPayableAmount(b.price, b.discount) ?? Infinity;
  if (priceA === priceB) {
    return 0;
  }
  return priceA - priceB;
};

export const toRatesByAvailability: MapToRates = (staticRates, availableRates) => {
  const rates = staticRates
    .map((rate) => {
      const availableRate = availableRates.find(({ id }) => id === rate.rateCode);
      return RateSchema.parse({
        id: availableRate?.id ?? rate.rateCode,
        price: availableRate?.from_price
          ? {
              ...toCamelCase(availableRate.from_price),
              amount:
                availableRate.discount?.standard_price.amount ?? availableRate.from_price.amount,
            }
          : toCamelCase(availableRate?.from_price),
        title: rate.name ?? '',
        description: rate.description ?? '',
        imageUrl: rate.images?.[0] ?? '',
        images: rate.images ?? [],
        cancellationPolicyText: rate.cancellationPolicyText,
        priority: rate.priority ?? null,
        unAvailable: !availableRate || !availableRate?.from_price,
        uuid: rate._id,
        package: parsePackage(availableRate?.package),
        paymentTerms: toCamelCase(availableRate?.payment_terms) ?? null,
        discount: availableRate?.discount ? toCamelCase(availableRate.discount) : undefined,
      });
    })
    // @ISSUE: casting and extra sort loop could be avoided by reducing to 'groups' rather than mapping..
    // @TODO: Break out and resturcture List/ListItem so can control upstream mapping
    // Unavailable rates always sort last (consumers such as SpaLandingPage don't filter them
    // out), then priority/price ordering applies within the available and unavailable groups.
    .sort((a, b) => {
      if (a.unAvailable !== b.unAvailable) {
        return a.unAvailable ? 1 : -1;
      }
      return byPriorityThenPrice(a, b);
    });

  return rates;
};
